import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CircularProgress } from "@mui/material"
import { RiArrowRightSLine } from "@remixicon/react"
import { endOfDay, format, isSameDay, isValid, subMonths } from "date-fns"

import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import { useFilteredActivitiesQuery } from "@/hooks/api/useActivityQuery"
import { getActivityColor } from "@/app/activity/list/utils"
import { useCalendarNav } from "@/app/home/hooks/useCalendarNav"
import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"
import Chip from "@/components/chip/Chip"
import type { Activity } from "@/types/activity"

function safeDate(value: string | undefined | null): Date | null {
  if (!value) {
    return null
  }
  const d = new Date(value)

  return Number.isNaN(d.getTime()) ? null : d
}

function formatDuration(dateStart: string, dateFinish: string): string {
  const start = safeDate(dateStart)
  const finish = safeDate(dateFinish)
  if (!start || !finish) {
    return "--:--:-- ~ --:--:--"
  }

  return `${format(start, "HH:mm:ss")} ~ ${format(finish, "HH:mm:ss")}`
}

interface DateGroup {
  dateKey: string
  date: Date
  activities: Activity[]
}

export default function ActivityScheduleWidget() {
  const { t } = useTranslation()
  const isPlayerAccount = createAccountStore((state) => state.info?.type === AccountTypeKey.Player)
  const { currentDate } = useCalendarNav()

  const [detailState, setDetailState] = useState<{
    open: boolean
    anchorEl: HTMLElement | null
    activity: Activity | null
  }>({ open: false, anchorEl: null, activity: null })
  const openDetail = (activity: Activity, anchorEl: HTMLElement) => {
    if (isPlayerAccount) {
      return
    }
    setDetailState({ open: true, anchorEl, activity })
  }
  const closeDetail = () => setDetailState((s) => ({ ...s, open: false }))

  // Activity List Schedule 뷰와 동일 정책: 롤링 1개월 (직전 1개월 ~ currentDate)
  const rangeStart = useMemo(() => subMonths(currentDate, 1).valueOf(), [currentDate])
  const rangeEnd = useMemo(() => endOfDay(currentDate).valueOf(), [currentDate])
  const { data: activities, isLoading } = useFilteredActivitiesQuery({
    start: rangeStart,
    end: rangeEnd,
  })

  const groups = useMemo<DateGroup[]>(() => {
    const map = new Map<string, DateGroup>()

    // 정책: 선택 날짜(currentDate) 이전 활동만 표시 — currentDate 가 첫 행이 되도록
    const cutoff = endOfDay(currentDate).getTime()

    ;(activities ?? []).forEach((item) => {
      const activity = item as unknown as Activity
      const date = safeDate(activity.dateStart)
      if (!date || date.getTime() > cutoff) {
        return
      }
      const key = format(date, "yyyy-MM-dd")
      if (!map.has(key)) {
        map.set(key, { dateKey: key, date, activities: [] })
      }
      map.get(key)!.activities.push(activity)
    })

    // 선택 날짜는 액티비티가 없어도 항상 포함 (Activity List view와 동일)
    const selectedKey = format(currentDate, "yyyy-MM-dd")
    if (!map.has(selectedKey)) {
      map.set(selectedKey, { dateKey: selectedKey, date: currentDate, activities: [] })
    }

    return Array.from(map.values()).sort((a, b) => b.date.getTime() - a.date.getTime())
  }, [activities, currentDate])

  const handleRowClick = (activity: Activity, anchorEl: HTMLElement) => {
    if (isPlayerAccount || !activity.idfActivity) {
      return
    }
    openDetail(activity, anchorEl)
  }

  if (isLoading) {
    return (
      <div className="ActivityScheduleWidget">
        <div className="ActivityScheduleWidget__empty">
          <CircularProgress size={24} />
        </div>
      </div>
    )
  }

  return (
    <div className="ActivityScheduleWidget">
      <div className="ActivityScheduleWidget__header">
        <span className="ActivityScheduleWidget__col-date">{t("date", "Date")}</span>
        <span className="ActivityScheduleWidget__col-name">
          {t("activityName", "Activity Name")}
        </span>
        <span className="ActivityScheduleWidget__col-duration">{t("duration", "Duration")}</span>
        <span className="ActivityScheduleWidget__col-count">{t("session", "Session")}</span>
        <span className="ActivityScheduleWidget__col-count">{t("player", "Player")}</span>
      </div>

      <div className="ActivityScheduleWidget__list">
        {groups.map((group) => {
          const isSelected = isValid(group.date) && isSameDay(group.date, currentDate)

          return (
            <div key={group.dateKey} className="ActivityScheduleWidget__group">
              <div className="ActivityScheduleWidget__date-cell">
                <span
                  className={`ActivityScheduleWidget__day-badge ${isSelected ? "ActivityScheduleWidget__day-badge--today" : ""}`}>
                  {format(group.date, "d")}
                </span>
                <span className="ActivityScheduleWidget__date-label">
                  {format(group.date, "MMM, EEE")}
                </span>
              </div>

              <div className="ActivityScheduleWidget__rows">
                {group.activities.map((activity) => (
                  <div
                    key={activity.idfActivity}
                    className={`ActivityScheduleWidget__row ${!isPlayerAccount ? "ActivityScheduleWidget__row--clickable" : ""}`}
                    onClick={(e) => handleRowClick(activity, e.currentTarget)}>
                    <div className="ActivityScheduleWidget__name-cell">
                      <span
                        className="ActivityScheduleWidget__dot"
                        style={{ background: getActivityColor(activity.type) }}
                      />
                      <span className="ActivityScheduleWidget__name-text">{activity.name}</span>
                      {activity.isLive && <Chip type="default-small" label={t("live", "Live")} />}
                      {!isPlayerAccount && (
                        <span className="ActivityScheduleWidget__name-arrow">
                          <RiArrowRightSLine size={16} />
                        </span>
                      )}
                    </div>
                    <span className="ActivityScheduleWidget__duration">
                      {formatDuration(activity.dateStart, activity.dateFinish)}
                    </span>
                    <span className="ActivityScheduleWidget__count">
                      {activity.sessionCount ?? 0}
                    </span>
                    <span className="ActivityScheduleWidget__count">
                      {activity.playerCount ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      {detailState.activity && (
        <ActivityInfoDialog
          open={detailState.open}
          anchorEl={detailState.anchorEl}
          activity={detailState.activity}
          onClose={closeDetail}
          onEdit={() => {}}
        />
      )}
    </div>
  )
}
